import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';

export default function HomePage() {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const faqs = [
    { q: '大满贯App是否免费？', a: '大满贯App完全免费下载和使用。我们提供免费的基础观赛服务，让每一位体育爱好者都能享受到高质量的赛事直播体验。部分高级功能可能需要订阅，但核心直播功能始终免费。' },
    { q: '支持哪些安卓机型？', a: '大满贯App支持所有运行Android 7.0及以上系统的安卓手机和平板设备，覆盖华为、小米、OPPO、vivo、三星等主流品牌全机型。' },
    { q: '如何安装APK安装包？', a: '下载APK文件后，请前往手机"设置-安全"中开启"允许安装未知来源应用"，然后点击下载的APK文件即可完成安装。安装完成后建议关闭此选项以保障设备安全。' },
    { q: '直播画质怎么样？流量消耗大吗？', a: '大满贯App采用先进的视频编码技术，提供标清、高清、超清多档画质选择。在WiFi环境下推荐使用超清画质，移动网络下可选择标清模式节省流量，每小时约消耗300MB-1GB不等。' },
    { q: '是否支持赛事回看？', a: '支持！所有直播赛事均提供赛后回看功能，您可以随时回顾精彩瞬间。回看视频通常在直播结束后30分钟内上线，支持倍速播放和关键节点跳转。' },
  ];

  const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);
  const latestArticles = articles.slice(0, 6);

  const features = [
    {
      icon: 'ri-live-line',
      title: t('features.live'),
      desc: t('features.live.desc'),
    },
    {
      icon: 'ri-line-chart-line',
      title: t('features.data'),
      desc: t('features.data.desc'),
    },
    {
      icon: 'ri-brain-line',
      title: t('features.analysis'),
      desc: t('features.analysis.desc'),
    },
    {
      icon: 'ri-group-line',
      title: t('features.community'),
      desc: t('features.community.desc'),
    },
  ];

  const homepageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '大满贯App',
        url: 'https://damanjuapp.com',
        logo: 'https://damanjuapp.com/logo.png',
        description: t('hero.desc'),
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: '+86-400-888-6666',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
        sameAs: [],
      },
      {
        '@type': 'WebSite',
        name: '大满贯App',
        url: 'https://damanjuapp.com',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://damanjuapp.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '大满贯App - 官方下载',
        description: t('hero.desc'),
        url: 'https://damanjuapp.com',
        isPartOf: { '@type': 'WebSite', name: '大满贯App' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="大满贯app安卓版下载|官方正版安装包"
        description="大满贯app安卓版官方下载，提供最新版APK安装包，支持安卓全机型，一键安装极速体验。海量赛事直播、实时数据、精准分析尽在大满贯安卓客户端，立即免费下载！"
        keywords="大满贯app安卓版,大满贯安卓下载,大满贯APK,大满贯官方下载,大满贯最新版,大满贯手机客户端,大满贯app下载"
        canonicalUrl="https://damanjuapp.com"
        schema={homepageSchema}
      />

      {/* Hero Section */}
      <section className="relative w-full overflow-hidden" aria-labelledby="hero-title">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Dynamic%20sports%20stadium%20panorama%20with%20dramatic%20lighting%2C%20athletes%20in%20motion%2C%20warm%20red%20and%20gold%20tones%2C%20cinematic%20sports%20atmosphere%2C%20epic%20scale%2C%20professional%20sports%20photography%2C%20crowd%20energy%2C%20heroic%20composition%2C%20Chinese%20sports%20aesthetics&width=1600&height=800&seq=hero-bg-home&orientation=landscape"
            alt="大满贯App - 体育赛事直播平台"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-32 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-background-50 text-sm mb-6">
            <i className="ri-android-line w-4 h-4 flex items-center justify-center"></i>
            <span>{t('hero.version')}</span>
          </div>
          <h1 id="hero-title" className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-background-50 mb-4 tracking-tight">
            {t('hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-background-200 mb-3 font-heading">
            {t('hero.subtitle')}
          </p>
          <p className="text-sm md:text-base text-background-300 max-w-2xl mb-8 leading-relaxed">
            {t('hero.desc')}
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <a
              href="#download"
              className="px-8 py-4 rounded-full bg-primary-500 text-background-50 text-lg font-semibold hover:bg-primary-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-primary-500/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-2"></i>
              {t('hero.download')}
            </a>
            <Link
              to="/category/all"
              className="px-8 py-4 rounded-full border-2 border-background-50/30 text-background-50 text-lg font-medium hover:bg-background-50/10 transition-all duration-300 whitespace-nowrap"
            >
              了解更多
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-background-50" aria-labelledby="features-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 id="features-title" className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-3">
              {t('features.title')}
            </h2>
            <p className="text-foreground-600 text-base">{t('features.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="group p-6 md:p-8 rounded-2xl bg-background-100 hover:bg-background-200/70 transition-all duration-300 text-center cursor-pointer"
              >
                <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-5 rounded-2xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${feature.icon} text-2xl text-primary-500 group-hover:text-background-50 transition-colors duration-300 w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-lg font-bold font-heading text-foreground-950 mb-2">{feature.title}</h3>
                <p className="text-foreground-600 text-sm leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-16 md:py-20 bg-background-100" aria-labelledby="articles-title">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-10">
            <div>
              <h2 id="articles-title" className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
                {t('articles.title')}
              </h2>
              <p className="text-foreground-600 text-sm">{t('articles.subtitle')}</p>
            </div>
            <Link
              to="/category/all"
              className="hidden sm:flex items-center gap-1 text-primary-500 hover:text-primary-600 font-medium text-sm transition-colors duration-200 whitespace-nowrap"
            >
              {t('articles.more')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group rounded-2xl overflow-hidden bg-background-50 hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                      {article.categoryName}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold font-heading text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-foreground-600 text-sm line-clamp-2 mb-4 leading-relaxed">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.date}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-3 h-3 flex items-center justify-center"></i>
                      阅读更多
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/all"
              className="inline-flex items-center gap-1 text-primary-500 font-medium text-sm whitespace-nowrap"
            >
              {t('articles.more')}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-14 md:py-18 bg-primary-500" aria-label="数据统计">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: '5000万+', label: '累计用户' },
              { num: '200+', label: '赛事覆盖' },
              { num: '99.9%', label: '直播稳定性' },
              { num: '4.9', label: '用户评分' },
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-3xl md:text-4xl font-bold text-background-50 mb-1 font-label">{stat.num}</div>
                <div className="text-background-200 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-16 md:py-20 bg-background-50" aria-label="最新文章">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-8">
            最新文章
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latestArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="group flex gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/70 transition-all duration-200"
              >
                <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                      {article.categoryName}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold font-heading text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200 mb-1">
                    {article.title}
                  </h3>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section id="download" className="relative overflow-hidden py-16 md:py-24" aria-labelledby="download-title">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20mobile%20app%20display%20on%20smartphone%20screen%20showing%20sports%20dashboard%2C%20clean%20minimal%20design%2C%20warm%20ambient%20lighting%2C%20dark%20elegant%20background%2C%20technology%20lifestyle%20photography%2C%20red%20accent%20highlights&width=1600&height=600&seq=cta-bg-download&orientation=landscape"
            alt="下载大满贯App"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 id="download-title" className="text-3xl md:text-4xl font-bold font-heading text-background-50 mb-3">
            {t('download.title')}
          </h2>
          <p className="text-background-300 text-base mb-8">{t('download.subtitle')}</p>
          <a
            href="#"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-accent-500 text-background-50 text-lg font-bold hover:bg-accent-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-accent-500/30 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-android-line text-xl w-5 h-5 flex items-center justify-center"></i>
            {t('download.btn')}
          </a>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-background-400 text-sm">
            <span className="flex items-center gap-1">
              <i className="ri-hard-drive-2-line w-4 h-4 flex items-center justify-center"></i>
              {t('download.size')}
            </span>
            <span className="hidden sm:block">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-smartphone-line w-4 h-4 flex items-center justify-center"></i>
              {t('download.requirement')}
            </span>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-20 bg-background-50" aria-labelledby="faq-title">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 id="faq-title" className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
              {t('faq.title')}
            </h2>
            <p className="text-foreground-600 text-sm">{t('faq.subtitle')}</p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-xl bg-background-100 border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left text-foreground-950 font-medium text-sm hover:bg-background-200/50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  <span>{faq.q}</span>
                  <i
                    className={`ri-arrow-down-s-line text-lg transition-transform duration-300 w-5 h-5 flex items-center justify-center ${
                      activeFaq === idx ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {activeFaq === idx && (
                  <div className="px-6 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}